import type { AppleAppRow } from '@/api/client'
import { useAppleApps } from '@/lib/queries'
import { count, roas } from '@/lib/money'
import { iosRevenueError, iosRevenueSkipped } from '@/lib/spend'
import type { JobRun } from '@/admin-design/_common/jobs/types'
import { DataTable, Money, Notice, When, errorText, useIsWide, type Column } from '@/ui'

/** The store revenue the table can show: not tried, or nothing pulled yet, is a dash. */
const noRevenue = (skipped: boolean, latest: string) => skipped || !latest

/** The revenue pull failed (red) or left rows out for want of a rate (amber).
 *  The Apple rows below still show: a failed pull says nothing about spend. */
function RevenueNotice({ runs }: { runs: JobRun[] | null | undefined }) {
  const err = iosRevenueError(runs)
  if (!err) return null
  return (
    <Notice
      tone={err.ok ? 'publishing' : 'failed'}
      title={err.ok ? '스토어 수익 일부를 합계에서 뺐다' : '스토어 수익 조회 오류 — 수익이 새로 들어오지 않는다'}
      detail={err.message}
    >
      {err.at && (
        <>
          마지막 시도 <When iso={err.at} mode="absolute" />.{' '}
        </>
      )}
      {err.ok ? '환율이 들어오면 다음 동기화에서 채워진다.' : '저장된 수익으로 계산한다. 다음 동기화가 성공하면 풀린다.'}
    </Notice>
  )
}

/** Apple Ads per app, with the App Store proceeds of the days the ads ran.
 *  Renders nothing without an Apple app row. */
export function AppleRoasSection({ runs }: { runs: JobRun[] | null | undefined }) {
  const q = useAppleApps(30)
  const wide = useIsWide()
  const rows = q.data?.rows ?? []
  if (q.error) return <Notice tone="failed" title="Apple 앱 ROAS를 읽지 못했다" detail={errorText(q.error)} />
  if (rows.length === 0) return null

  const latest = q.data?.latest_revenue_date ?? ''
  const hidden = noRevenue(iosRevenueSkipped(runs), latest)

  const columns: Column<AppleAppRow>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (a) => (
        <div className="min-w-0">
          <div className="max-w-56 truncate font-medium" title={a.name}>
            {a.name}
          </div>
          <div className="text-meta tabular-nums text-muted-foreground">{a.adam_id}</div>
        </div>
      ),
    },
    {
      key: 'campaigns',
      header: '캠페인',
      card: 'line',
      cell: (a) => (
        <span className="whitespace-nowrap tabular-nums">
          운영 중 {a.enabled}·전체 {a.campaigns}
        </span>
      ),
    },
    { key: 'spend', header: '지출', align: 'right', card: 'line', cell: (a) => <Money value={a.spend_usd} /> },
    { key: 'installs', header: '설치', align: 'right', card: 'line', cell: (a) => <span className="tabular-nums">{count(a.installs)}</span> },
    { key: 'cpi', header: 'CPI', align: 'right', card: 'line', cell: (a) => <Money value={a.installs > 0 ? a.cpi : null} hidden={a.installs <= 0} /> },
    {
      key: 'proceeds',
      header: '광고 나간 날 수익',
      align: 'right',
      card: 'line',
      cell: (a) =>
        hidden ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="inline-flex flex-col items-end">
            <Money value={a.matched_proceeds_usd} />
            <span className="text-meta text-muted-foreground">{a.matched_days}일</span>
          </span>
        ),
    },
    {
      key: 'roas',
      header: 'ROAS',
      align: 'right',
      card: 'line',
      cell: (a) => (
        <span className="whitespace-nowrap font-medium tabular-nums">{hidden || a.roas == null ? '—' : roas(a.roas, a.matched_spend_usd)}</span>
      ),
    },
  ]

  return (
    <section aria-label="Apple 앱 ROAS" className="space-y-3">
      <div>
        <h2 className="text-section font-semibold">
          Apple 앱 ROAS · 최근 30일
          <span className="ml-2 text-table font-normal text-muted-foreground">{rows.length}개</span>
        </h2>
        <p className="mt-0.5 text-table text-muted-foreground">
          광고가 나간 날의 앱 전체 수익 기준이라 자연 유입 수익이 섞인다.
          {latest ? ` 스토어 수익은 ${latest}까지 집계됐다.` : ' 스토어 수익은 아직 들어오지 않았다.'}
        </p>
      </div>
      <RevenueNotice runs={runs} />
      <DataTable label="Apple 앱 ROAS" columns={columns} rows={rows} rowKey={(a) => a.adam_id} mode={wide ? 'table' : 'cards'} />
    </section>
  )
}

